/**
 * Kreator — powłoka modala pełnoekranowego dla podglądu "na żywo" (makieta).
 *
 * Skórka (wygląd) na wspólnym rdzeniu `Overlay`, jak `Modal`, ale dużo większy panel
 * (Modal.tsx kapuje na `max-w-4xl` — za mało na jednoczesny podgląd formularza + listy
 * kartoteki). Nie jest wariantem `ModalSize` bo to inny przypadek użycia (pełnoekranowy
 * overlay roboczy, nie dialog).
 *
 * Zero logiki biznesowej — czysta powłoka. Zawartość (formularz/lista) wpina
 * strona wywołująca przez `children`.
 */

import { type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import { IconButton } from '@/components/IconButton';
import { Overlay } from '@/components/Overlay';

export interface PreviewModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children?: ReactNode;
  closeOnBackdrop?: boolean;
  closeOnEsc?: boolean;
}

export function PreviewModal({
  open,
  onClose,
  title,
  children,
  closeOnBackdrop = true,
  closeOnEsc = true,
}: PreviewModalProps) {
  const { t } = useTranslation();
  const titleId = useId();

  return (
    <Overlay
      open={open}
      onClose={onClose}
      closeOnBackdrop={closeOnBackdrop}
      closeOnEsc={closeOnEsc}
      backdropClassName="z-50 flex items-center justify-center bg-black/50 p-4"
      panelClassName="flex h-[calc(100vh-3rem)] w-full max-w-[95vw] flex-col rounded-2xl border border-border-secondary bg-bg-elevated shadow-[0_18px_50px_rgba(31,41,55,0.28)] dark:shadow-[0_18px_50px_rgba(0,0,0,0.6)]"
      labelledBy={titleId}
      testId="preview-modal"
    >
      <header className="flex items-center justify-between gap-4 border-b border-border-secondary px-5 py-4">
        <div className="flex min-w-0 items-center gap-3">
          <h2 id={titleId} className="truncate text-lg font-semibold text-text-primary">
            {title}
          </h2>
          <span className="inline-flex shrink-0 items-center rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-medium text-accent-700 dark:bg-accent-900/40 dark:text-accent-300">
            {t('preview.badge')}
          </span>
        </div>
        <IconButton
          size="sm"
          variant="ghost"
          onClick={onClose}
          aria-label={t('preview.close')}
          data-testid="preview-modal-close"
          className="flex-shrink-0"
        >
          <X aria-hidden="true" />
        </IconButton>
      </header>
      <div className="flex-1 overflow-y-auto px-5 py-4 text-sm text-text-primary">{children}</div>
    </Overlay>
  );
}
